import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, Check, X, Archive, Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ideaVideoStylesApi } from "@/lib/api";

// Video-style dynamic list — cloned 1:1 from CategoriesDialog. In-use
// styles are archived instead of hard-deleted so historic ideas keep
// their label.
export function VideoStylesDialog({ open, onOpenChange, styles, onChanged }) {
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [busyLabel, setBusyLabel] = useState(null);

  // Load "Other — describe…" free-text labels that users submitted on ideas
  // and haven't been promoted or dismissed yet.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await ideaVideoStylesApi.suggestions();
        setSuggestions(res.suggestions || []);
      } catch {
        setSuggestions([]);
      }
    })();
  }, [open]);

  const reloadSuggestions = async () => {
    try {
      const res = await ideaVideoStylesApi.suggestions();
      setSuggestions(res.suggestions || []);
    } catch {
      /* no-op */
    }
  };

  const handlePromote = async (label) => {
    setBusyLabel(label);
    try {
      await ideaVideoStylesApi.promote(label);
      toast.success(`Promoted "${label}"`);
      await Promise.all([onChanged?.(), reloadSuggestions()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally {
      setBusyLabel(null);
    }
  };

  const handleDismiss = async (label) => {
    if (!window.confirm(`Dismiss "${label}"? Ideas using this label will lose their style tag (the idea itself stays).`))
      return;
    setBusyLabel(label);
    try {
      const res = await ideaVideoStylesApi.dismiss(label);
      toast.success(`Cleared ${res.cleared_count} idea${res.cleared_count === 1 ? "" : "s"}`);
      await reloadSuggestions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed");
    } finally {
      setBusyLabel(null);
    }
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await ideaVideoStylesApi.create(name);
      toast.success(`Added "${name}"`);
      setNewName("");
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const handleRename = async (id) => {
    const name = editingName.trim();
    if (!name) return;
    setBusyId(id);
    try {
      await ideaVideoStylesApi.update(id, { name });
      toast.success("Renamed");
      setEditingId(null);
      setEditingName("");
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (style) => {
    if (
      !window.confirm(
        `Delete "${style.name}"? Styles in use will be archived (past ideas keep their label).`
      )
    )
      return;
    setBusyId(style.id);
    try {
      const res = await ideaVideoStylesApi.remove(style.id);
      toast.success(res.archived ? `Archived "${style.name}"` : `Deleted "${style.name}"`);
      await onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="video-styles-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            Manage video styles
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Styles are shared across every client. Renames cascade so old
            ideas keep matching labels.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 pt-2">
          <Input
            data-testid="new-video-style-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New video style"
            className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
          />
          <Button
            data-testid="add-video-style-btn"
            onClick={handleAdd}
            disabled={!newName.trim() || adding}
            className="bg-amber-500 text-black hover:bg-amber-400"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>

        {/* Suggested-from-ideas — free-text labels users typed via "Other" */}
        {suggestions.length > 0 && (
          <div
            data-testid="video-styles-suggestions"
            className="mt-4 border border-amber-500/30 rounded-md bg-amber-500/5 p-3"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-mono uppercase tracking-wider text-amber-300">
                Suggested from ideas · {suggestions.length}
              </span>
            </div>
            <ul className="divide-y divide-amber-500/10">
              {suggestions.map((s) => (
                <li
                  key={s.label}
                  data-testid={`suggestion-row-${s.label}`}
                  className="flex items-center gap-2 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{s.label}</div>
                    <div className="text-[11px] text-zinc-500">
                      used on {s.count} idea{s.count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    data-testid={`suggestion-promote-${s.label}`}
                    onClick={() => handlePromote(s.label)}
                    disabled={busyLabel === s.label}
                    className="bg-emerald-500 text-black hover:bg-emerald-400 h-7"
                  >
                    {busyLabel === s.label ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5 mr-1" /> Promote
                      </>
                    )}
                  </Button>
                  <button
                    type="button"
                    data-testid={`suggestion-dismiss-${s.label}`}
                    onClick={() => handleDismiss(s.label)}
                    disabled={busyLabel === s.label}
                    className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-zinc-800"
                    title="Dismiss (clears the label from all ideas)"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul className="mt-4 divide-y divide-zinc-800/70 border border-zinc-800 rounded-md">
          {styles.length === 0 ? (
            <li className="p-4 text-center text-sm text-zinc-500 italic">
              No styles yet.
            </li>
          ) : (
            styles.map((s) => (
              <li
                key={s.id}
                data-testid={`video-style-row-${s.id}`}
                className="flex items-center gap-2 p-3 hover:bg-zinc-900/40"
              >
                {editingId === s.id ? (
                  <>
                    <Input
                      data-testid={`edit-video-style-input-${s.id}`}
                      value={editingName}
                      autoFocus
                      onChange={(e) => setEditingName(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleRename(s.id);
                        }
                      }}
                    />
                    <button
                      data-testid={`save-video-style-${s.id}`}
                      onClick={() => handleRename(s.id)}
                      disabled={busyId === s.id}
                      className="p-1.5 rounded-sm text-emerald-400 hover:bg-zinc-800"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditingName("");
                      }}
                      className="p-1.5 rounded-sm text-zinc-500 hover:bg-zinc-800"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100">
                        {s.name}
                        {s.is_default && (
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            default
                          </span>
                        )}
                        {s.is_archived && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500"
                            data-testid={`video-style-archived-${s.id}`}
                          >
                            <Archive className="w-3 h-3" /> archived
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      data-testid={`edit-video-style-${s.id}`}
                      onClick={() => {
                        setEditingId(s.id);
                        setEditingName(s.name);
                      }}
                      className="p-1.5 rounded-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`delete-video-style-${s.id}`}
                      onClick={() => handleDelete(s)}
                      disabled={busyId === s.id}
                      className="p-1.5 rounded-sm text-zinc-500 hover:text-rose-400 hover:bg-zinc-800"
                    >
                      {busyId === s.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </>
                )}
              </li>
            ))
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
